import React, { useState, useEffect } from 'react';
import { FiSearch } from 'react-icons/fi';
import InstructorCard from '../../homes/InstructorCard';
import { Menu, MenuHandler, MenuItem, MenuList } from '@material-tailwind/react';
import { useNavigate } from 'react-router-dom';
import Pagination from '../../../components/Pagination/Pagination';
import instructorService from '../../../services/instructorService';

const sortOptions = ['Default', 'Rating', 'Students', 'Courses'];

function MainContent() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All Categories');
  const [selectedSort, setSelectedSort] = useState('Sort by: Default');
  const [instructors, setInstructors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const navigate = useNavigate();

  const instructorsPerPage = 8;

  // Charger les catégories au montage du composant
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const categoriesData = await instructorService.getCategories();
        setCategories(categoriesData);
      } catch (error) {
        console.error("Erreur lors du chargement des catégories:", error);
      }
    };
    
    loadCategories();
  }, []);

  // Charger les instructeurs
  const loadInstructors = React.useCallback(async (page = 1) => {
    setIsLoading(true);
    try {
      const filters = {
        search: searchQuery,
        category: selectedCategory !== 'All Categories' ? selectedCategory : null,
        sort: selectedSort.replace('Sort by: ', '') !== 'Default' 
              ? selectedSort.replace('Sort by: ', '') 
              : null,
        page,
        limit: instructorsPerPage
      };
      
      const response = await instructorService.getAllInstructors(filters);
      setInstructors(response.instructors);
      setTotalCount(response.totalCount);
      setCurrentPage(response.currentPage);
      setTotalPages(response.totalPages);
    } catch (error) {
      console.error("Erreur lors du chargement des instructeurs:", error);
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery, selectedCategory, selectedSort]);

  // Charger les instructeurs au montage et quand les filtres changent
  useEffect(() => {
    loadInstructors(1);
  }, [loadInstructors]);

  const handleInstructorClick = (instructorId) => {
    navigate(`/instructors-single/${instructorId}`);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    loadInstructors(page);
  };

  return (
    <section className="py-12 dark:bg-dark-1">
      <div className="container mx-auto px-4">
        {/* Filters Section */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 pb-8">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Showing <span className="font-medium text-gray-900 dark:text-white">{totalCount}</span> total results
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search Input */}
            <div className="relative h-[50px] flex items-center">
              <input
                type="text"
                placeholder="Search Instructors"
                className="pl-4 pr-10 py-2 bg-gray-200 dark:bg-gray-700 rounded-lg w-full sm:w-64 h-[50px]"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <FiSearch className="absolute right-3 top-2.5 text-gray-500 dark:text-gray-400" />
            </div>

            {/* Category Dropdown */}
            <Menu>
              <MenuHandler>
                <button className="flex justify-between items-center bg-light-3 text-sm text-gray-500 h-[50px] 
                px-5 py-[10px] w-[146px] rounded-md">
                  {selectedCategory}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 ml-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              </MenuHandler>
              <MenuList className="bg-white px-5 py-[10px] w-[146px]">
                <MenuItem
                  onClick={() => setSelectedCategory('All Categories')}
                  className='hover:text-blue-600 px-[15px] pt-[8px] w-fit text-sm items-center text-gray-500'
                >
                  All Categories
                </MenuItem>
                {categories.map((category) => (
                  <MenuItem
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className='hover:text-blue-600 px-[15px] pt-[8px] w-fit text-sm items-center text-gray-500'
                  >
                    {category}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>

            {/* Sort Dropdown */}
            <Menu>
              <MenuHandler>
                <button className="flex justify-between items-center bg-light-3 text-sm text-gray-500 h-[50px] 
                px-5 py-[10px] w-[206px] rounded-md">
                  {selectedSort}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 ml-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              </MenuHandler>
              <MenuList className="bg-white px-5 py-[10px] w-[206px]">
                {sortOptions.map((option) => (
                  <MenuItem
                    key={option}
                    onClick={() => setSelectedSort(`Sort by: ${option}`)}
                    className='hover:text-blue-600 px-[15px] pt-[8px] w-fit text-sm items-center text-gray-500'
                  >
                    {option}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        )}

        {/* Instructors Grid */}
        {!isLoading && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {instructors.map((instructor) => (
                <InstructorCard 
                  key={instructor.id} 
                  {...instructor} 
                  onClick={() => handleInstructorClick(instructor.id)} 
                />
              ))}
            </div>

            {/* No results message */}
            {instructors.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500 dark:text-gray-400 text-lg">No instructors found matching your criteria.</p>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            )}
          </>
        )}
      </div>
    </section>
  );
}

export default MainContent;